import type { BandName, PersonaProfile } from '../shared/types';

export type Outcome =
  | 'transfer_clean' | 'transfer_soft' | 'transfer_dropped'
  | 'closed_clean' | 'closed_soft' | 'closed_cancelled'
  | 'qualified_out' | 'no_show' | 'lost';

const ORDER: BandName[] = ['cold', 'cool', 'neutral', 'warm', 'hot'];
export const bandRank = (band: BandName) => ORDER.indexOf(band);
export const bandAtLeast = (band: BandName, floor: BandName) => bandRank(band) >= bandRank(floor);

/**
 * §10.5 — the setter's transfer *is* the close, resolved against the band at
 * initiation. `transfer_dropped` is the expensive one: the full lead cost was
 * spent and the closer never spoke to anyone.
 */
const SETTER: Record<BandName, Outcome> = {
  hot: 'transfer_clean',
  warm: 'transfer_soft',
  neutral: 'transfer_dropped',
  cool: 'lost',
  cold: 'lost',
};

const CLOSER: Record<BandName, Outcome> = {
  hot: 'closed_clean',
  warm: 'closed_soft',
  neutral: 'closed_cancelled',
  cool: 'lost',
  cold: 'lost',
};

/** The 5% scheduled path: the appointment is the close, and a set at Cool or below is a no-show. */
const SCHEDULED: Record<BandName, Outcome> = {
  hot: 'transfer_clean',
  warm: 'transfer_soft',
  neutral: 'transfer_soft',
  cool: 'no_show',
  cold: 'no_show',
};

const SUCCESSFUL = new Set<Outcome>([
  'transfer_clean', 'transfer_soft', 'closed_clean', 'closed_soft',
]);

export function resolveOutcome(args: {
  role: string;
  band: BandName;
  persona: PersonaProfile | null;
  /** An option may carry its own terminal outcome, which overrides the band. */
  authored?: string | null;
  scheduled?: boolean;
}): Outcome {
  const { role, band, persona, authored, scheduled } = args;

  // §5.4 / §10.5 — a qualify-out is available wherever it is authored, and it
  // passes. A rep who recognizes that someone should not buy, and says so, is
  // the highest-value behavior the system can certify.
  if (authored === 'qualified_out') return 'qualified_out';
  if (authored) return authored as Outcome;

  const table = scheduled ? SCHEDULED : role === 'closer' ? CLOSER : SETTER;
  const outcome = table[band];

  // §5.4 — closing a vulnerable persona below Hot produces closed_cancelled,
  // regardless of the org's configured minimumCloseBand. Warmth earned cheaply
  // from someone who should not have bought is not a sale; it is a refund.
  if (persona?.vulnerable && band !== 'hot' && SUCCESSFUL.has(outcome)) {
    return 'closed_cancelled';
  }
  return outcome;
}

/**
 * §13.2 condition 3 — the outcome half of a pass: `qualified_out`, or a close
 * band at or above the org's configured minimum. The other conditions
 * (subscores, compliance, severe confusion) are evaluated with the full record.
 */
export function outcomePasses(
  outcome: Outcome,
  band: BandName,
  minimumCloseBand: BandName
): boolean {
  if (outcome === 'qualified_out') return true;
  if (!SUCCESSFUL.has(outcome)) return false;
  return bandAtLeast(band, minimumCloseBand);
}

/** Plain language for the rep. `transfer_dropped` is rendered plainly on purpose (§10.5). */
export const OUTCOME_IS_GOOD: Record<Outcome, boolean> = {
  transfer_clean: true,
  transfer_soft: false,
  transfer_dropped: false,
  closed_clean: true,
  closed_soft: false,
  closed_cancelled: false,
  qualified_out: true,
  no_show: false,
  lost: false,
};
